import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { EntityCombobox, type EntityOption } from '@/components/shared/entity-combobox';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { churchToday, formatDate } from '@/utils/format';

import type { AssetRow } from './asset-dialog';
import { AssetsSubnav } from './assets-subnav';

interface RentalRow {
  id: string;
  assetId: string;
  borrowerName: string;
  borrowerMemberId: string | null;
  startsOn: string;
  dueOn: string;
  returnedOn: string | null;
  status: 'reserved' | 'active' | 'returned' | 'cancelled';
  purpose: string | null;
}

interface MemberOption {
  id: string;
  firstName: string;
  lastName: string;
}

export const RentalsPage = () => {
  const { t } = useTranslation('assets');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canManage = hasPermission(membership, PERMISSIONS.ASSETS_MANAGE_RENTALS);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [returnTarget, setReturnTarget] = useState<RentalRow | null>(null);
  const [returnedOn, setReturnedOn] = useState<string | null>(churchToday());

  const [assetId, setAssetId] = useState<string | null>(null);
  const [borrowerName, setBorrowerName] = useState('');
  const [borrowerMemberId, setBorrowerMemberId] = useState<string | null>(null);
  const [borrowerSelected, setBorrowerSelected] = useState<EntityOption | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [startsOn, setStartsOn] = useState<string | null>(churchToday());
  const [dueOn, setDueOn] = useState<string | null>(null);
  const [purpose, setPurpose] = useState('');

  const list = useQuery({
    queryKey: ['assets', 'rentals', page, pageSize],
    queryFn: () =>
      api.get<{ data: RentalRow[]; page: OffsetPage }>('/assets/rentals', {
        query: { page, pageSize },
      }),
    placeholderData: keepPreviousData,
  });

  const assets = useQuery({
    queryKey: ['assets', 'picker'],
    queryFn: () =>
      api.get<{ data: AssetRow[] }>('/assets', {
        query: { pageSize: 100, status: 'active' },
      }),
  });

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', 'asset-rental', memberQuery],
    enabled: createOpen,
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '20' });
      if (memberQuery.trim()) params.set('q', memberQuery.trim());
      return api.get<{ data: MemberOption[] }>(`/people/members?${params.toString()}`);
    },
  });

  const assetNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const asset of assets.data?.data ?? []) {
      map.set(asset.id, asset.name);
    }
    return map;
  }, [assets.data?.data]);

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [members.data?.data],
  );

  const resetCreate = () => {
    setAssetId(null);
    setBorrowerName('');
    setBorrowerMemberId(null);
    setBorrowerSelected(null);
    setMemberQuery('');
    setStartsOn(churchToday());
    setDueOn(null);
    setPurpose('');
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<RentalRow>('/assets/rentals', {
        assetId,
        borrowerName: borrowerName.trim(),
        borrowerMemberId,
        startsOn,
        dueOn,
        purpose: purpose.trim() || null,
      }),
    onSuccess: async () => {
      toast.success(t('rentals.saved'));
      setCreateOpen(false);
      resetCreate();
      await queryClient.invalidateQueries({ queryKey: ['assets', 'rentals'] });
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const activate = useMutation({
    mutationFn: (rentalId: string) =>
      api.post<RentalRow>(`/assets/rentals/${rentalId}/activate`),
    onSuccess: async () => {
      toast.success(t('rentals.activated'));
      await queryClient.invalidateQueries({ queryKey: ['assets', 'rentals'] });
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const markReturned = useMutation({
    mutationFn: () =>
      api.post<RentalRow>(`/assets/rentals/${returnTarget!.id}/return`, {
        returnedOn,
      }),
    onSuccess: async () => {
      toast.success(t('rentals.returned'));
      setReturnTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['assets', 'rentals'] });
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const columns: DataTableColumn<RentalRow>[] = [
    {
      id: 'startsOn',
      header: t('rentals.columns.startsOn'),
      cell: (row) => formatDate(row.startsOn),
    },
    {
      id: 'asset',
      header: t('rentals.columns.asset'),
      cell: (row) => assetNameById.get(row.assetId) ?? '—',
    },
    {
      id: 'borrower',
      header: t('rentals.columns.borrower'),
      cell: (row) => (
        <MemberNameLink memberId={row.borrowerMemberId} name={row.borrowerName} />
      ),
    },
    {
      id: 'dueOn',
      header: t('rentals.columns.dueOn'),
      cell: (row) => formatDate(row.dueOn),
    },
    {
      id: 'status',
      header: t('rentals.columns.status'),
      cell: (row) => (
        <Badge variant="neutral">{t(`rentals.status.${row.status}`)}</Badge>
      ),
    },
    {
      id: 'actions',
      header: t('rentals.columns.actions'),
      cell: (row) =>
        canManage ? (
          <div className="flex justify-end gap-2">
            {row.status === 'reserved' ? (
              <Button
                size="sm"
                variant="outline"
                onClick={(event) => {
                  event.stopPropagation();
                  activate.mutate(row.id);
                }}
              >
                {t('rentals.activate')}
              </Button>
            ) : null}
            {row.status === 'active' ? (
              <Button
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  setReturnedOn(churchToday());
                  setReturnTarget(row);
                }}
              >
                {t('rentals.return')}
              </Button>
            ) : null}
          </div>
        ) : null,
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;
  const canSubmitCreate =
    Boolean(assetId) && borrowerName.trim().length > 0 && Boolean(startsOn) && Boolean(dueOn);

  return (
    <div>
      <PageHeader
        title={t('rentals.title')}
        description={t('rentals.description')}
        headerActions={
          canManage ? (
            <Button onClick={() => setCreateOpen(true)}>{t('rentals.create')}</Button>
          ) : null
        }
      />

      <AssetsSubnav />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={
          <div className="space-y-3 py-4 text-center">
            <p className="text-body font-medium">{t('rentals.empty.title')}</p>
            <p className="text-body-sm text-fg-secondary">{t('rentals.empty.description')}</p>
            {canManage ? (
              <div className="flex justify-end sm:justify-center">
                <Button onClick={() => setCreateOpen(true)}>{t('rentals.empty.action')}</Button>
              </div>
            ) : null}
          </div>
        }
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <div className="mt-4">
          <Pagination
            page={pageMeta.page}
            pageSize={pageMeta.pageSize}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) =>
                tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <FormDialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetCreate();
        }}
        title={t('rentals.create')}
        submitLabel={t('rentals.save')}
        cancelLabel={tCommon('action.cancel')}
        submitting={create.isPending}
        submitDisabled={!canSubmitCreate}
        size="lg"
        onSubmit={() => create.mutate()}
      >
        <FormField label={t('rentals.fields.asset')} required>
          {(field) => (
            <Listbox
              id={field.id}
              value={assetId ?? ''}
              onChange={(value) => setAssetId(value || null)}
              options={(assets.data?.data ?? []).map((asset) => ({
                value: asset.id,
                label: asset.name,
              }))}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('rentals.fields.borrowerName')} required>
          {(field) => (
            <Input
              id={field.id}
              value={borrowerName}
              onChange={(event) => setBorrowerName(event.target.value)}
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('rentals.fields.borrowerMember')}>
          {(field) => (
            <EntityCombobox
              id={field.id}
              value={borrowerMemberId}
              onChange={(id) => {
                setBorrowerMemberId(id);
                const option = memberOptions.find((row) => row.id === id) ?? null;
                setBorrowerSelected(option);
                if (option && !borrowerName.trim()) {
                  setBorrowerName(option.label);
                }
              }}
              options={memberOptions}
              selected={borrowerSelected}
              onQueryChange={setMemberQuery}
              loading={members.isLoading}
              placeholder={tCommon('action.search')}
              emptyMessage={tCommon('search.noResults')}
              clearLabel={tCommon('action.clearSelection')}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('rentals.fields.startsOn')} required>
          {(field) => (
            <DatePicker
              id={field.id}
              value={startsOn}
              onChange={setStartsOn}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('rentals.fields.dueOn')} required>
          {(field) => (
            <DatePicker
              id={field.id}
              value={dueOn}
              onChange={setDueOn}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('rentals.fields.purpose')}>
          {(field) => (
            <Textarea
              id={field.id}
              value={purpose}
              onChange={(event) => setPurpose(event.target.value)}
              rows={3}
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>
      </FormDialog>

      <FormDialog
        open={Boolean(returnTarget)}
        onOpenChange={(open) => {
          if (!open) setReturnTarget(null);
        }}
        title={t('rentals.returnTitle')}
        description={t('rentals.returnDescription')}
        submitLabel={t('rentals.returnConfirm')}
        cancelLabel={tCommon('action.cancel')}
        submitting={markReturned.isPending}
        submitDisabled={!returnedOn}
        size="md"
        onSubmit={() => markReturned.mutate()}
      >
        <FormField label={t('rentals.fields.returnedOn')} required>
          {(field) => (
            <DatePicker
              id={field.id}
              value={returnedOn}
              onChange={setReturnedOn}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>
      </FormDialog>
    </div>
  );
};
